<template>
  <el-checkbox-group
    v-model="model"
    :size="size"
    :disabled="isDisabled"
    :min="min"
    :max="max"
    :text-color="textColor"
    :fill="fill"
    @change="onChange">
    <component
      :is="button ? 'el-checkbox-button' : 'el-checkbox'"
      v-for="item in checkboxOptions"
      :key="item[itemKey]"
      :label="item[itemKey]"
      :border="border"
      :disabled="item.disabled">
      {{item[itemLabel]}}
    </component>
  </el-checkbox-group>
</template>

<script>
/* eslint-disable semi */
import { Component, Prop } from 'vue-property-decorator';
import SasComponent from './SasComponent';
import * as sas from '@/core/decorators';

@Component
export default class SasCheckboxGroup extends SasComponent {
  /*
   チェックボックス要素についてはカスタマイズ不可です

   下記はコンポーネント側で管理します
   el-checkbox-group.min
   el-checkbox-group.max
   el-checkbox-group.textColor
   el-checkbox-group.fill
   el-checkbox.border
  */

  /**
   * 親がv-modelでバインドしたデータの格納先プロパティ
   * v-model対応
   */
  @Prop({ required: true })
  value;

  @Prop({ required: false, type: String })
  size;

  @Prop({ required: false, type: Boolean })
  disabled;

  /**
   * v-model対応
   */
  get model () {
    // nullの場合は空の配列を返却する
    return (this.value === null) ? [] : this.value;
  }

  /**
   * v-model対応
   */
  set model (value) {
    this.$emit('input', value);
  }

  get min () {
    return undefined;
  }

  get max () {
    return undefined;
  }

  get textColor () {
    return '#ffffff';
  }

  get fill () {
    return '#409EFF';
  }

  /**
   * 枠線の有無
   *
   * NOTE
   * 枠線を表示する場合は、このメソッドをオーバーライドして、trueを返却する。
   */
  get border () {
    return false;
  }

  /**
   * el-checkbox-buttonを利用するか否
   *
   * NOTE
   * el-checkbox-buttonを利用する場合は、このメソッドをオーバーライドして、trueを返却する。
   */
  get button () {
    return false;
  }

  /**
   * 有効無効状態を参照する
   *
   * NOTE
   * 親コンポーネントからPropで指定された状態と、自身が管理する状態
   * の双方を参照して有効無効状態を算出しています。
   */
  get isDisabled () {
    return this.disabled || this.innerDisabled;
  }

  /**
   * 選択項目要素のキー項目
   *
   * NOTE
   * キー項目として他の項目の値を参照する必要がある場合は、このメソッドをオーバー
   * ライドして、任意の項目名を返却する。
   */
  get itemKey () {
    return 'value';
  }

  /**
   * 選択項目要素のラベル項目
   *
   * NOTE
   * ラベル項目として他の項目の値を参照する必要がある場合は、このメソッドをオーバー
   * ライドして、任意の項目名を返却する。
   */
  get itemLabel () {
    return 'label';
  }

  get autofocus () {
    return true;
  }

  // NOTE
  // loading: コンポーネント自身のローディングフラグ
  // remoteLoading: 動的ローディング用のローディングフラグ
  @sas.BindLoading({ activeOn: ['fetch*'] })
  @sas.BindCache({ transparent: true })
  @sas.BindApi
  @sas.BindDefault
  data () {
    return {
      checkboxOptions: [],
      innerDisabled: false,
      loading: false
    };
  }

  // 初期化
  initialize () {
    this.fetchOptions();
  }

  /**
   * 選択オプションを取得する
   *
   * NOTE
   * 動的ローディングが有効な場合、自身のvalueに対応する選択オプションを取得する
   * 処理として実装する。
   */
  @sas.Asynchronous
  fetchOptions () {
    return this.fetchOptionsImpl()
      .then(options => {
        this.checkboxOptions = options;
      })
      .catch(reason => {
        if (!reason.isCanceled) {
          this.innerDisabled = true;
          this.checkboxOptions = this.$_sas.$default.checkboxOptions;
        }
        this.ignore(reason);
      });
  }

  /**
   * 選択オプションをサーバから取得する
   *
   * NOTE
   * この処理は継承クラス側で個別に実装する。
   * 成功時にはoptionsを返却するPromiseを返却するメソッドとして実装する。
   */
  fetchOptionsImpl () {
    throw new Error('SasCheckboxGroup.fetchOptionsImpl must be overridden.');
  }

  // TODO フォーカス設定できない
  focus () {
    const children = this.$children;
    for (let idx in children) {
      if (!children[idx].disabled && children[idx].focus) {
        children[idx].focus();
      }
    }
  }

  // 親コンポーネントへの透過イベント
  onChange (...args) {
    this.$emit('change', ...args);
  }
}
</script>



// WEBPACK FOOTER //
// src/core/ui/SasCheckboxGroup.vue